import "../components/cpl-code-editor";
import "../components/cpl-markdown-editor";
import { TableColumns } from "../components/table-columns";
import { LitElement, html, svg, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  api,
  type JsonObject,
  type KnowledgeCatalog,
  type KnowledgeUpdateRepository,
} from "../api/client";
import { session } from "../auth/session";
import "../components/knowledge-update-dialog";
import { i18n } from "../i18n";
import { readable } from "../view-model";
import { waitForLoadingCycle } from "../components/table-loading";
import "../components/app-dialog";
import {
  KnowledgeReference,
  referenceValues,
  withLocation,
} from "../components/knowledge-reference";
import "../components/a2k-autocomplete/a2k-autocomplete";
import "../components/app-pagination";
import {
  nextTableSort,
  tableSortQuery,
  sortableTableHeader,
  type TableSort,
} from "../components/table-sort";

type ViewKey =
  | "search"
  | "contracts"
  | "evidence"
  | "symbols"
  | "chunks"
  | "files"
  | "documents"
  | "locations"
  | "components"
  | "repositories"
  | "cases"
  | "ingestion-sources";
type Resource = Exclude<ViewKey, "search" | "ingestion-sources" | "documents">;
type FieldKind =
  "text" | "textarea" | "number" | "boolean" | "select" | "lines" | "search";
interface FieldDef {
  name: string;
  kind?: FieldKind;
  required?: boolean;
  choices?: string[];
  resource?: Resource; // For search fields: which resource to search
  displayField?: string; // For search fields: which field to display
}

const referenceResources: Record<string, string> = {
  repository: "repository",
  repositories: "repository",
  component: "component",
  namespace: "namespace",
  file_id: "file",
  location_id: "location",
  symbol_id: "symbol",
  evidence_id: "evidence",
  contracts: "contract",
  evidence: "evidence",
  ingestion_source: "ingestion_source",
};
const fields: Record<Resource, FieldDef[]> = {
  repositories: [
    { name: "repository_key", required: true },
    { name: "name", required: true },
    { name: "url" },
    { name: "platform" },
  ],
  components: [
    { name: "repository" },
    { name: "component_key", required: true },
    { name: "name", required: true },
  ],
  files: [
    { name: "namespace", required: true },
    { name: "relative_path", required: true },
    { name: "suffix" },
    { name: "language", choices: ["en", "source", "zh-cn"] },
    {
      name: "file_kind",
      kind: "text",
      choices: [
        "build_or_config",
        "cangjie_source",
        "compiler_definition",
        "document",
        "documentation",
        "docx",
        "markdown",
        "native_source",
        "other",
        "pdf",
      ],
    },
    { name: "text", kind: "textarea" },
  ],
  locations: [
    { name: "file_id", required: true, kind: "search", resource: "files" },
    { name: "line_start", kind: "number" },
    { name: "line_end", kind: "number" },
    { name: "page_start", kind: "number" },
    { name: "page_end", kind: "number" },
    { name: "section" },
  ],
  chunks: [
    {
      name: "location_id",
      required: true,
      kind: "search",
      resource: "locations",
    },
    {
      name: "kind",
      required: true,
      kind: "select",
      choices: ["document", "source"],
    },
    { name: "language", kind: "select" },
    { name: "title_path" },
    { name: "text", kind: "textarea", required: true },
  ],
  symbols: [
    { name: "repository" },
    { name: "component", kind: "search", resource: "components" },
    {
      name: "location_id",
      required: true,
      kind: "search",
      resource: "locations",
    },
    { name: "kind", required: true, kind: "select" },
    { name: "qualified_name" },
    { name: "simple_name" },
    { name: "signature", kind: "textarea" },
    { name: "visibility", kind: "select" },
    { name: "summary", kind: "textarea" },
    { name: "annotations", kind: "lines" },
  ],
  evidence: [
    { name: "repository" },
    { name: "component", kind: "search", resource: "components" },
    {
      name: "location_id",
      required: true,
      kind: "search",
      resource: "locations",
    },
    { name: "level", kind: "select" },
    { name: "confidence", kind: "select" },
    { name: "source_type" },
    { name: "extractor" },
    { name: "text", kind: "textarea", required: true },
  ],
  contracts: [
    { name: "kind", required: true, kind: "select" },
    {
      name: "family",
      required: true,
      kind: "select",
      choices: ["declaration", "deployment", "document", "abi", "package"],
    },
    { name: "repository" },
    { name: "component", kind: "search", resource: "components" },
    { name: "location_id", kind: "search", resource: "locations" },
    { name: "symbol_id", kind: "search", resource: "symbols" },
    { name: "name" },
    { name: "visibility", kind: "select" },
    { name: "stability", kind: "select" },
    { name: "risk_surface", kind: "select" },
    { name: "confidence", kind: "select" },
    { name: "summary", kind: "textarea" },
  ],
  cases: [
    { name: "decision" },
    { name: "decision_reason", kind: "textarea" },
    { name: "review_status" },
    { name: "repositories", kind: "lines" },
    { name: "pull_requests", kind: "lines" },
    { name: "contracts", kind: "lines" },
    { name: "evidence", kind: "lines" },
    { name: "rule_hits", kind: "lines" },
  ],
};
const details: Record<string, FieldDef[]> = {
  declaration: [
    { name: "public_commitment", kind: "boolean" },
    { name: "compatibility_rule", kind: "textarea" },
    { name: "since_version" },
    { name: "declaration_text", kind: "textarea" },
  ],
  deployment: [
    "deployment_model",
    "contract_scope",
    "packaging_model",
    "forward_risk",
    "legacy_note",
    "forward_check",
  ].map(
    (name) =>
      ({
        name,
        kind:
          name.includes("note") || name.includes("check") ? "textarea" : "text",
      }) as FieldDef,
  ),
  document: ["title_path", "section_name", "rule_text", "target_gate"].map(
    (name) =>
      ({ name, kind: name === "rule_text" ? "textarea" : "text" }) as FieldDef,
  ),
  abi: [
    "abi_subject",
    "layout_rule",
    "calling_convention",
    "runtime_symbol",
    "target_gate",
  ].map(
    (name) =>
      ({
        name,
        kind: name === "layout_rule" ? "textarea" : "text",
      }) as FieldDef,
  ),
  package: [
    "package_name",
    "module_name",
    "export_name",
    "system_capability",
    "target_gate",
  ].map((name) => ({ name })),
};
const columns: Record<Exclude<ViewKey, "search">, string[]> = {
  contracts: [
    "id",
    "name",
    "kind",
    "family",
    "repository",
    "component",
    "risk_surface",
  ],
  evidence: ["id", "level", "repository", "relative_path", "text"],
  symbols: ["id", "qualified_name", "kind", "repository", "relative_path"],
  chunks: ["id", "kind", "repository", "relative_path", "text"],
  files: [
    "relative_path",
    "file_kind",
    "has_markdown",
    "language",
    "size_bytes",
  ],
  documents: ["relative_path", "namespace", "converted_at", "id"],
  locations: ["id", "file_id", "relative_path", "line_start", "line_end"],
  components: ["id", "name", "repository", "symbol_count", "contract_count"],
  repositories: ["id", "name", "platform", "component_count", "file_count"],
  cases: ["id", "decision", "review_status", "version", "updated_at"],
  "ingestion-sources": [
    "id",
    "source_key",
    "source_group",
    "source_format",
    "resource_count",
  ],
};
const filters: Partial<Record<ViewKey, string[]>> = {
  documents: ["q", "namespace"],
  contracts: ["q", "repository", "component", "kind", "family", "risk"],
  evidence: ["q", "repository", "component", "level"],
  symbols: ["q", "repository", "component", "kind"],
  chunks: ["repository", "file_id", "kind"],
  files: [
    "q",
    "relative_path",
    "suffix",
    "file_kind",
    "has_markdown",
    "language",
    "namespace",
  ],
  locations: ["file_id"],
  components: ["repository"],
  repositories: ["q"],
  "ingestion-sources": ["q", "source_group", "source_format"],
};
const singular: Record<string, string> = {
  repositories: "repository",
  components: "component",
  files: "file",
  documents: "document",
  locations: "location",
  chunks: "chunk",
  symbols: "symbol",
  contracts: "contract",
  evidence: "evidence",
  cases: "case",
  "ingestion-sources": "ingestion_source",
};
const defaultSorts: Record<Exclude<ViewKey, "search">, TableSort> = {
  contracts: { key: "id", direction: "asc" },
  evidence: { key: "id", direction: "asc" },
  symbols: { key: "qualified_name", direction: "asc" },
  chunks: { key: "id", direction: "asc" },
  files: { key: "relative_path", direction: "asc" },
  documents: { key: "path", direction: "asc" },
  locations: { key: "relative_path", direction: "asc" },
  components: { key: "id", direction: "asc" },
  repositories: { key: "id", direction: "asc" },
  cases: { key: "updated_at", direction: "asc" },
  "ingestion-sources": { key: "source_key", direction: "asc" },
};
const zhFieldLabels: Record<string, string> = {
  q: "关键词",
  types: "资源类型",
  match: "匹配模式",
  minimum_should_match: "最少匹配词数",
  id: "ID",
  name: "名称",
  kind: "类别",
  family: "契约族",
  repository: "仓库",
  repository_key: "仓库标识",
  component: "组件",
  component_key: "组件标识",
  namespace: "来源空间",
  relative_path: "相对路径",
  path: "路径",
  url: "网址",
  platform: "平台",
  suffix: "扩展名",
  language: "语言",
  file_kind: "文件类别",
  content_sha256: "内容哈希",
  size_bytes: "文件大小",
  has_markdown: "Markdown 内容",
  markdown_document_id: "关联 Markdown 文档",
  file_id: "来源文件",
  location_id: "来源位置",
  line_start: "起始行",
  line_end: "结束行",
  page_start: "起始页",
  page_end: "结束页",
  section: "章节",
  title_path: "标题路径",
  text: "正文",
  qualified_name: "限定名称",
  simple_name: "短名称",
  signature: "签名",
  visibility: "可见性",
  summary: "摘要",
  annotations: "注解",
  level: "证据级别",
  confidence: "可信度",
  source_type: "来源类型",
  extractor: "提取器",
  symbol_id: "关联符号",
  stability: "稳定性",
  risk: "风险面",
  risk_surface: "风险面",
  decision: "判定",
  decision_reason: "判定原因",
  review_status: "审核状态",
  repositories: "仓库",
  pull_requests: "拉取请求",
  contracts: "契约",
  evidence: "证据",
  rule_hits: "命中规则",
  public_commitment: "公开承诺",
  compatibility_rule: "兼容规则",
  since_version: "起始版本",
  declaration_text: "声明正文",
  deployment_model: "部署模型",
  contract_scope: "契约范围",
  packaging_model: "打包模型",
  forward_risk: "前向风险",
  legacy_note: "历史说明",
  forward_check: "前向检查",
  section_name: "章节名称",
  rule_text: "规则正文",
  target_gate: "目标条件",
  abi_subject: "ABI 主体",
  layout_rule: "布局规则",
  calling_convention: "调用约定",
  runtime_symbol: "运行时符号",
  package_name: "包名称",
  module_name: "模块名称",
  export_name: "导出名称",
  system_capability: "系统能力",
  evidence_id: "关联证据",
  relation: "关系",
  trigger_condition: "触发条件",
  old_observable: "原行为",
  new_observable: "新行为",
  consumer_dependency_risk: "消费者依赖风险",
  compatibility_sensitive: "兼容性敏感",
  symbol_name: "符号名称",
  ingestion_source: "导入来源",
  observed_commit: "观测提交",
  resource_type: "资源类型",
  title: "标题",
  citation: "引用",
  source_group: "来源分组",
  source_format: "来源格式",
  source_key: "来源标识",
  resource_count: "资源数量",
  component_count: "组件数量",
  file_count: "文件数量",
  symbol_count: "符号数量",
  contract_count: "契约数量",
  version: "版本",
  updated_at: "更新时间",
  converted_at: "转换时间",
};

@customElement("knowledge-view")
export class KnowledgeView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private view: ViewKey = "search";
  @state() private catalog: KnowledgeCatalog | null = null;
  @state() private items: JsonObject[] = [];
  @state() private query: Record<string, string> = {};
  @state() private page = 1;
  @state() private pageSize = 10;
  @state() private sorts = { ...defaultSorts };
  @state() private totalPages = 0;
  @state() private totalCount = 0;
  @state() private busy = false;
  @state() private error = "";
  @state() private notice = "";
  @state() private detail: JsonObject | null = null;
  @state() private detailBusy = false;
  @state() private editor: JsonObject | null = null;
  @state() private editing: JsonObject | null = null;
  @state() private evidenceRows: JsonObject[] = [];
  @state() private relationEditor: "behaviors" | "consumers" | null = null;
  @state() private relation: JsonObject = {};
  @state() private observations: JsonObject[] = [];
  @state() private showUploadDialog = false;
  @state() private uploadFile: File | null = null;
  @state() private uploadNamespace = "";
  @state() private uploadRelativePath = "";
  @state() private uploadError: string | null = null;
  @state() private searchResults: Record<string, JsonObject[]> = {};
  @state() private searchLoading: Record<string, boolean> = {};
  @state() private searchDisplayValue: Record<string, string> = {};
  @state() private confirmDelete: JsonObject | null = null;
  @state() private suggestions: JsonObject[] = [];
  @state() private fieldSuggestions: Record<string, string[]> = {};
  @state() private suggestionBusy = false;
  @state() private suggestionError = "";
  @state() private suggestionIndex = -1;
  @state() private selectedRepositories = new Map<
    string,
    KnowledgeUpdateRepository
  >();
  @state() private updateRepositories: KnowledgeUpdateRepository[] = [];
  @state() private updateOpen = false;
  private sequence = 0;
  private suggestionSequence = 0;
  private detailSequence = 0;
  private fieldSuggestionSequence: Record<string, number> = {};
  private suggestionTimer?: number;
  private readonly languageChanged = () => this.requestUpdate();
  protected createRenderRoot() {
    return this;
  }
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.initialize();
  }
  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    window.clearTimeout(this.suggestionTimer);
    super.disconnectedCallback();
  }
  private async initialize() {
    if (new URLSearchParams(window.location.search).get("view") === "documents")
      this.selectView("documents");
    try {
      this.catalog = (await api.knowledgeCatalog()).catalog;
    } catch (e) {
      this.error = (e as Error).message;
    }
  }
  private selectView(view: ViewKey) {
    const legacyDocuments = view === "documents";
    this.view = legacyDocuments ? "files" : view;
    this.detailSequence += 1;
    this.sequence += 1;
    this.query = legacyDocuments ? { has_markdown: "true" } : {};
    this.page = 1;
    this.items = [];
    this.detail = null;
    this.error = "";
    this.selectedRepositories = new Map();
    if (view !== "search") void this.load(1);
  }
  private selectRepository(item: JsonObject, checked: boolean) {
    const id = String(item.id || "");
    if (!id) return;
    const selected = new Map(this.selectedRepositories);
    if (checked)
      selected.set(id, {
        id,
        label: String(item.name || item.repository_key || id),
      });
    else selected.delete(id);
    this.selectedRepositories = selected;
  }
  private selectRepositoryPage(checked: boolean) {
    for (const item of this.items) this.selectRepository(item, checked);
  }
  private openKnowledgeUpdate() {
    if (
      session.user?.role !== "administrator" ||
      !this.selectedRepositories.size
    )
      return;
    this.updateRepositories = [...this.selectedRepositories.values()].map(
      (repo) => ({ ...repo }),
    );
    this.updateOpen = true;
  }
  private async load(page = this.page) {
    if (this.view === "search" && !this.query.q?.trim()) {
      this.error = i18n.t("knowledgeQueryRequired");
      return;
    }
    const id = ++this.sequence;
    const loadingStarted = performance.now();
    this.busy = true;
    this.error = "";
    try {
      const sort = this.view === "search" ? null : this.sorts[this.view];
      const req = {
        ...this.query,
        ...(sort ? tableSortQuery(sort) : {}),
        page,
        page_size: this.pageSize,
      };
      const data =
        this.view === "search"
          ? await api.knowledgeSearch(req)
          : this.view === "documents"
            ? await api.documents(req)
            : await api.knowledgeList(this.view, req);
      if (id !== this.sequence) return;

      this.items = data.items;
      this.page = data.page;
      this.totalPages = data.total_pages;
      this.totalCount = data.total_count;
    } catch (e) {
      if (id === this.sequence) {
        this.items = [];
        this.error = (e as Error).message;
      }
    } finally {
      if (id === this.sequence) {
        await waitForLoadingCycle(loadingStarted);
        if (id === this.sequence) this.busy = false;
      }
    }
  }
  private resourceFor(item: JsonObject): Resource | "documents" {
    if (
      this.view === "documents" ||
      String(item.id || "").startsWith("markdown:")
    )
      return "documents";
    if (this.view !== "search") return this.view as Resource;
    const t = String(item.resource_type || "");
    return (t === "chunk" ? "chunks" : `${t}s`) as Resource;
  }
  private async openDetail(item: JsonObject) {
    const resource = this.resourceFor(item);
    const detailSequence = ++this.detailSequence;
    this.detail = item;
    this.detailBusy = true;
    try {
      if (resource === "documents") {
        const metadata = await api.document(String(item.id));
        const content = await api.documentContent(String(item.id));
        if (detailSequence === this.detailSequence)
          this.detail = {
            ...metadata.document,
            content: content.document.content,
          };
        return;
      }
      const r = await api.knowledgeDetail(
        resource,
        String(item.id),
        String(item.repository || ""),
      );
      if (detailSequence !== this.detailSequence) return;
      const value = (r[singular[resource]] as JsonObject) || r;

      // For all file resources, simplify the view
      if (resource === "files") {
        let content = value.text;
        if (value.markdown_document_id) {
          const document = await api.documentContent(
            String(value.markdown_document_id),
          );
          if (detailSequence !== this.detailSequence) return;
          content = document.document.content;
        }
        this.detail = {
          id: value.id,
          repository: value.repository,
          namespace: value.namespace,
          relative_path: value.relative_path,
          file_kind: value.file_kind,
          language: value.language,
          text: content,
          markdown_document_id: value.markdown_document_id,
          has_markdown: value.has_markdown,
          converted_at: value.converted_at,
          content_format: value.content_format,
          size_bytes: value.size_bytes,
          suffix: value.suffix,
        };
      } else {
        this.detail =
          resource === "cases" ? await this.expandCaseRelations(value) : value;
      }
    } catch (e) {
      if (detailSequence === this.detailSequence) {
        this.error = (e as Error).message;
        this.detail = null;
      }
    } finally {
      if (detailSequence === this.detailSequence) this.detailBusy = false;
    }
  }
  private async expandCaseRelations(value: JsonObject) {
    const expand = async (resource: "contracts" | "evidence") => {
      const relations = value[resource];
      if (!Array.isArray(relations)) return relations;
      return Promise.all(
        relations.map(async (relation) => {
          if (typeof relation === "object" && relation) return relation;
          const id = String(relation || "");
          if (!id) return relation;
          try {
            const response = await api.knowledgeDetail(resource, id);
            return (response[singular[resource]] as JsonObject) || response;
          } catch {
            return { id };
          }
        }),
      );
    };
    const [contracts, evidence] = await Promise.all([
      expand("contracts"),
      expand("evidence"),
    ]);
    return { ...value, contracts, evidence };
  }
  private label(name: string) {
    if (i18n.language === "zh-CN" && zhFieldLabels[name])
      return zhFieldLabels[name];
    const key = `knowledgeField_${name}`;
    const translated = i18n.t(key as never);
    return translated || name.replaceAll("_", " ");
  }
  private async fetchSuggestions(value: string) {
    if (value.trim().length < 2) {
      this.suggestions = [];
      this.suggestionBusy = false;
      return;
    }
    const id = ++this.suggestionSequence;
    this.suggestionBusy = true;
    this.suggestionError = "";
    try {
      const r = await api.knowledgeSuggestions({
        q: value.trim(),
        types: this.query.types,
        limit: 10,
      });
      if (id !== this.suggestionSequence) return;
      this.suggestions = r.items;
      this.suggestionIndex = -1;
    } catch (e) {
      if (id !== this.suggestionSequence) return;
      this.suggestions = [];
      this.suggestionError = (e as Error).message;
    } finally {
      if (id === this.suggestionSequence) this.suggestionBusy = false;
    }
  }

  private async fetchFieldSuggestions(field: string, value: string) {
    const sequence = (this.fieldSuggestionSequence[field] || 0) + 1;
    this.fieldSuggestionSequence[field] = sequence;
    if (value.trim().length < 2) {
      this.fieldSuggestions = { ...this.fieldSuggestions, [field]: [] };
      return;
    }
    try {
      const result = await api.knowledgeSuggestions({
        q: value.trim(),
        field,
        limit: 12,
      });
      if (this.fieldSuggestionSequence[field] !== sequence) return;
      this.fieldSuggestions = {
        ...this.fieldSuggestions,
        [field]: result.items.map((item) =>
          String(item.value || item.label || ""),
        ),
      };
    } catch {
      if (this.fieldSuggestionSequence[field] !== sequence) return;
      this.fieldSuggestions = { ...this.fieldSuggestions, [field]: [] };
    }
  }
  private updateSearch(value: string) {
    this.query = { ...this.query, q: value };
    window.clearTimeout(this.suggestionTimer);
    this.suggestionTimer = window.setTimeout(
      () => void this.fetchSuggestions(value),
      250,
    );
  }
  private chooseSuggestion(item: JsonObject) {
    this.query = { ...this.query, q: String(item.value || item.label || "") };
    this.suggestions = [];
    void this.load(1);
  }
  private closeSuggestions(event: FocusEvent) {
    const current = event.currentTarget as HTMLElement;
    if (
      event.relatedTarget instanceof Node &&
      current.contains(event.relatedTarget)
    )
      return;
    this.suggestions = [];
    this.suggestionIndex = -1;
  }
  private searchKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this.suggestions = [];
      return;
    }
    if (!this.suggestions.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      this.suggestionIndex =
        (this.suggestionIndex + step + this.suggestions.length) %
        this.suggestions.length;
    } else if (e.key === "Enter" && this.suggestionIndex >= 0) {
      e.preventDefault();
      this.chooseSuggestion(this.suggestions[this.suggestionIndex]);
    }
  }
  private suggestionPanel() {
    if ((this.query.q?.length || 0) < 2) return nothing;
    if (
      !this.suggestionBusy &&
      !this.suggestionError &&
      !this.suggestions.length
    )
      return nothing;
    return html`<div
      id="knowledge-search-suggestions"
      class="knowledge-suggestions"
      role="listbox"
    >
      ${
        this.suggestionBusy
          ? html`<span>${i18n.t("knowledgeSuggestionsLoading")}</span>`
          : this.suggestionError
            ? html`<span role="alert"
                >${i18n.t("knowledgeSuggestionsError")}</span
              >`
            : this.suggestions.map(
                (item, i) =>
                  html`<button
                    id=${`knowledge-suggestion-${i}`}
                    type="button"
                    role="option"
                    aria-selected=${String(i === this.suggestionIndex)}
                    @mousedown=${(e: Event) => e.preventDefault()}
                    @click=${() => this.chooseSuggestion(item)}
                  >
                    <strong>${item.label}</strong>
                    <small
                      >${item.resource_type || ""}${item.id ? ` · ${item.id}` : ""}</small
                    >
                  </button>`,
              )
      }
    </div>`;
  }
  private filter(name: string) {
    if (name === "has_markdown")
      return html`<label class="native-field"
        ><span>${i18n.t("knowledgeHasMarkdown")}</span>
        <select
          class="a2k-native-input"
          .value=${this.query[name] || ""}
          @change=${(event: Event) => {
            const value = (event.target as HTMLSelectElement).value;
            this.query = { ...this.query, [name]: value };
          }}
        >
          <option value="">${i18n.t("knowledgeAll")}</option>
          <option value="true">${i18n.t("knowledgeYes")}</option>
          <option value="false">${i18n.t("knowledgeNo")}</option>
        </select></label
      >`;
    if (referenceResources[name])
      return this.referenceField(
        name,
        this.query[name],
        (value) => {
          this.query = { ...this.query, [name]: String(value) };
          if (name === "repository")
            for (const field of [
              "component",
              "file_id",
              "location_id",
              "symbol_id",
              "evidence_id",
            ])
              this.query[field] = "";
        },
        this.query,
      );
    let choices: string[] = [];
    if (name === "types") choices = this.catalog?.search.types || [];
    if (name === "match") choices = this.catalog?.search.match || [];
    if (name === "kind" && this.view === "symbols")
      choices = this.catalog?.facets.symbol_kind || [];
    if (name === "level") choices = this.catalog?.facets.evidence_level || [];
    if (name === "risk") choices = this.catalog?.facets.risk_surface || [];
    if (name === "q" && this.view === "search")
      return html`<div
        class="native-field knowledge-search-field"
        @focusout=${this.closeSuggestions}
      >
        <label for="knowledge-search-input">${this.label(name)}</label>
        <input
          id="knowledge-search-input"
          class="a2k-native-input"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="knowledge-search-suggestions"
          aria-activedescendant=${
            this.suggestionIndex >= 0
              ? `knowledge-suggestion-${this.suggestionIndex}`
              : nothing
          }
          aria-expanded=${String(Boolean(this.suggestions.length || this.suggestionBusy || this.suggestionError))}
          .value=${this.query.q || ""}
          @input=${(e: Event) => this.updateSearch((e.target as HTMLInputElement).value)}
          @keydown=${this.searchKeydown}
          @focus=${() => {
            if (this.query.q?.trim().length >= 2 && !this.suggestions.length)
              void this.fetchSuggestions(this.query.q);
          }}
        />${this.suggestionPanel()}
      </div>`;
    const values = this.fieldSuggestions[name] || choices;
    return html`<label class="native-field"
      ><span>${this.label(name)}</span>${
        choices.length
          ? html`<select
              class="a2k-native-input"
              .value=${this.query[name] || ""}
              @change=${(e: Event) => (this.query = { ...this.query, [name]: (e.target as HTMLSelectElement).value })}
            >
              <option value="">${i18n.t("knowledgeAll")}</option>
              ${choices.map((x) => html`<option value=${x}>${x}</option>`)}
            </select>`
          : html`<a2k-autocomplete
              .value=${this.query[name] || ""}
              .options=${values}
              ariaLabel=${this.label(name)}
              @value-change=${(e: CustomEvent<string>) => {
                this.query = { ...this.query, [name]: e.detail };
                void this.fetchFieldSuggestions(name, e.detail);
              }}
            ></a2k-autocomplete>`
      }</label
    >`;
  }
  private detailValue(value: unknown): TemplateResult {
    if (Array.isArray(value))
      return html`<div class="knowledge-detail-array">
        ${
          value.length
            ? value.map((x) =>
                typeof x === "object" && x
                  ? html`<div class="knowledge-detail-item">
                      ${this.detailObject(x as JsonObject)}
                    </div>`
                  : html`<span>${readable(x)}</span>`,
              )
            : i18n.t("empty")
        }
      </div>`;
    if (typeof value === "object" && value)
      return this.detailObject(value as JsonObject);
    return html`<span>${readable(value)}</span>`;
  }
  private downloadText(value: JsonObject) {
    const blob = new Blob([String(value.content ?? value.text ?? "")], {
      type: "text/markdown;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = String(value.relative_path || "document.md")
      .split("/")
      .pop()!
      .replace(/\.(pdf|docx)$/i, ".md");
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  private detailObject(value: JsonObject) {
    const resource = this.resourceFor(value);
    if (resource === "files" || resource === "documents") {
      const fileKind = String(value.file_kind || "").toLowerCase();
      const isMarkdown =
        resource === "documents" ||
        Boolean(value.has_markdown) ||
        fileKind === "markdown" ||
        /\.(md|markdown)$/i.test(String(value.relative_path || ""));
      const fileLanguage = /\.py$/i.test(String(value.relative_path || ""))
        ? "python"
        : /\.(js|ts|mjs|cjs)$/i.test(String(value.relative_path || ""))
          ? "javascript"
          : /\.json$/i.test(String(value.relative_path || ""))
            ? "json"
            : /\.(sh|bash)$/i.test(String(value.relative_path || ""))
              ? "bash"
              : "text";
      if (
        value.content != null ||
        value.text != null ||
        resource === "documents" ||
        value.has_markdown ||
        fileKind === "pdf" ||
        fileKind === "docx" ||
        fileKind === "document" ||
        fileKind === "markdown"
      ) {
        const metadata = { ...value };
        delete metadata.text;
        delete metadata.content;
        delete metadata.contracts;
        delete metadata.symbols;
        delete metadata.chunks;
        delete metadata.locations;

        return html`
          <div class="file-detail-split">
            <div class="file-detail-sidebar">
              <h4>${i18n.t("knowledgeFileMetadata")}</h4>
              <dl class="detail-list">
                ${Object.entries(metadata)
                  .filter(([k]) => !k.startsWith("_"))
                  .map(
                    ([k, v]) =>
                      html`<dt>${this.label(k)}</dt>
                        <dd>${this.detailValue(v)}</dd>`,
                  )}
              </dl>
            </div>
            <div class="file-detail-content">
              <h4>${i18n.t("knowledgeFileContent")}</h4>
              ${
                String(value.content ?? value.text ?? "").length > 200000
                  ? html`<p>${i18n.t("knowledgePreviewLimited")}</p>
                      <a2k-button @click=${() => this.downloadText(value)}
                        >${i18n.t("knowledgeDownloadContent")}</a2k-button
                      >`
                  : nothing
              }
              ${
                value.has_markdown &&
                String(value.content ?? value.text ?? "").length <= 200000
                  ? html`<a2k-button @click=${() => this.downloadText(value)}
                      >${i18n.t("knowledgeDownloadContent")}</a2k-button
                    >`
                  : nothing
              }
              <div class="file-text-scroll">
                ${
                  isMarkdown
                    ? html`<cpl-markdown-editor
                        .value=${String(value.content ?? value.text ?? "").slice(0, 200000)}
                        .readOnly=${true}
                        .label=${i18n.t("knowledgeFileContent")}
                        .minHeight=${"20rem"}
                      ></cpl-markdown-editor>`
                    : html`<cpl-code-editor
                        .value=${String(value.content ?? value.text ?? "").slice(0, 200000)}
                        .language=${fileLanguage}
                        .readOnly=${true}
                        .label=${i18n.t("knowledgeFileContent")}
                        .minHeight=${"20rem"}
                      ></cpl-code-editor>`
                }
              </div>
            </div>
          </div>
        `;
      }
    }

    return html`<dl class="detail-list">
      ${Object.entries(value).map(
        ([k, v]) =>
          html`<dt>${this.label(k)}</dt>
            <dd>${this.detailValue(v)}</dd>`,
      )}
    </dl>`;
  }
  private startCreate() {
    this.editing = null;
    this.editor = {};
    this.evidenceRows = [];
  }
  private startUpload() {
    this.showUploadDialog = true;
    this.uploadFile = null;
    this.uploadNamespace = "";
    this.uploadRelativePath = "";
    this.uploadError = null;
  }
  private handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadFile = input.files[0];
      if (!this.uploadRelativePath) {
        this.uploadRelativePath = this.uploadFile.name;
      }
    }
  }
  private async submitUpload() {
    if (
      !this.querySelector<KnowledgeReference>(
        "knowledge-reference.upload-namespace",
      )?.reportValidity()
    )
      return;
    if (!this.uploadFile || !this.uploadNamespace) {
      this.uploadError = i18n.t("formFieldsRequired");
      return;
    }

    if (
      !/\.(md|txt|pdf|docx)$/i.test(this.uploadFile.name) ||
      this.uploadFile.size > 20 * 1024 * 1024
    ) {
      this.uploadError = i18n.t("knowledgeUploadLimits");
      return;
    }
    const formData = new FormData();
    formData.append("file", this.uploadFile);
    formData.append("namespace", this.uploadNamespace);
    formData.append(
      "relative_path",
      this.uploadRelativePath || this.uploadFile.name,
    );

    try {
      this.busy = true;
      this.uploadError = null;

      await api.uploadKnowledgeFile(formData);

      this.showUploadDialog = false;
      this.uploadRelativePath ||= this.uploadFile?.name || "";
      this.uploadFile = null;
      this.view = "files";
      this.query = {
        namespace: this.uploadNamespace,
        relative_path: this.uploadRelativePath,
        has_markdown: "true",
      };
      this.detail = null;
      this.detailSequence += 1;
      await this.load(1);
    } catch (e) {
      this.uploadError = (e as Error).message;
    } finally {
      this.busy = false;
    }
  }
  private startEdit(item: JsonObject) {
    this.editing = item;
    this.editor = { ...item };
    this.evidenceRows = [];
    void this.hydrateEditor(item);
  }
  private async hydrateEditor(item: JsonObject) {
    try {
      const r = await api.knowledgeDetail(
        this.view,
        String(item.id),
        String(item.repository || ""),
      );
      const v = (r[singular[this.view]] as JsonObject) || r;
      this.editing = v;
      this.editor = { ...v, ...((v.detail as JsonObject) || {}) };
      this.evidenceRows = Array.isArray(v.evidence)
        ? v.evidence.map((x) => ({ ...(x as JsonObject) }))
        : [];
    } catch (e) {
      this.error = (e as Error).message;
    }
  }
  private setEditor(name: string, value: unknown) {
    this.editor = { ...(this.editor || {}), [name]: value };
    if (name === "family" && this.view === "contracts") {
      const choices = this.fieldChoices({ name: "kind", required: true });
      if (choices.length && !choices.includes(String(this.editor.kind || "")))
        this.editor.kind = "";
    }
  }
  private fieldChoices(def: FieldDef): string[] {
    const preserveOptional = (values: string[]) => {
      const current = this.editor?.[def.name];
      return !def.required && current && !values.includes(String(current))
        ? [...values, String(current)]
        : values;
    };
    if (def.choices) return preserveOptional(def.choices);
    if (def.name === "kind" && this.view === "contracts") {
      const kinds = (
        this.catalog?.resources.contracts as JsonObject | undefined
      )?.kinds;
      return Array.isArray(kinds)
        ? kinds
            .filter(
              (kind: JsonObject) =>
                !this.editor?.family || kind.family === this.editor.family,
            )
            .map((kind: JsonObject) => String(kind.value))
        : [];
    }
    const facet =
      def.name === "kind" && this.view === "symbols"
        ? "symbol_kind"
        : def.name === "level"
          ? "evidence_level"
          : def.name;
    return preserveOptional(this.catalog?.facets[facet] || []);
  }
  private input(def: FieldDef) {
    const choices = this.fieldChoices(def);
    const value = this.editor?.[def.name];
    if (def.name === "location_id")
      return this.locationField(
        this.editor || {},
        (value) => {
          this.editor = { ...this.editor, ...value };
        },
        def.required,
      );
    if (referenceResources[def.name])
      return this.referenceField(
        def.name,
        value,
        (selected) => {
          this.setEditor(def.name, selected);
          if (def.name === "repository")
            for (const field of [
              "component",
              "location_id",
              "symbol_id",
              "evidence_id",
              "file_id",
            ])
              this.setEditor(field, "");
        },
        this.editor || {},
        def.required,
        ["repositories", "contracts", "evidence"].includes(def.name),
      );
    if (def.kind === "boolean")
      return html`<label class="knowledge-check"
        ><input
          type="checkbox"
          .checked=${Boolean(value)}
          @change=${(e: Event) => this.setEditor(def.name, (e.target as HTMLInputElement).checked)}
        /><span>${this.label(def.name)}</span></label
      >`;
    if (def.kind === "select" && choices.length)
      return html`<label class="native-field"
        ><span>${this.label(def.name)}${def.required ? " *" : ""}</span
        ><select
          class="a2k-native-input"
          .value=${String(value || "")}
          @change=${(e: Event) => this.setEditor(def.name, (e.target as HTMLSelectElement).value)}
        >
          <option value="">${i18n.t("knowledgeSelect")}</option>
          ${choices.map((x) => html`<option value=${x}>${x}</option>`)}
        </select></label
      >`;
    if (def.kind === "textarea" || def.kind === "lines")
      return html`<label class="native-field knowledge-form-wide"
        ><span>${this.label(def.name)}${def.required ? " *" : ""}</span
        ><textarea
          class="a2k-native-input"
          .value=${def.kind === "lines" && Array.isArray(value) ? value.join("\n") : String(value || "")}
          @input=${(e: Event) =>
            this.setEditor(
              def.name,
              def.kind === "lines"
                ? (e.target as HTMLTextAreaElement).value
                    .split("\n")
                    .map((x) => x.trim())
                    .filter(Boolean)
                : (e.target as HTMLTextAreaElement).value,
            )}
        ></textarea
        >${
          def.kind === "lines"
            ? html`<small>${i18n.t("knowledgeOnePerLine")}</small>`
            : nothing
        }</label
      >`;
    const autocompleteValues: string[] = choices.length
      ? choices
      : this.fieldSuggestions[def.name] || [];
    return html`<label class="native-field"
      ><span>${this.label(def.name)}${def.required ? " *" : ""}</span>${
        def.kind === "number"
          ? html`<input
              class="a2k-native-input"
              type="number"
              .value=${value == null ? "" : String(value)}
              @input=${(e: Event) => {
                const input = e.target as HTMLInputElement;
                this.setEditor(
                  def.name,
                  input.value ? Number(input.value) : null,
                );
              }}
            />`
          : html`<a2k-autocomplete
              .value=${value == null ? "" : String(value)}
              .options=${autocompleteValues}
              ariaLabel=${this.label(def.name)}
              @value-change=${(e: CustomEvent<string>) => {
                this.setEditor(def.name, e.detail);
                void this.fetchFieldSuggestions(def.name, e.detail);
              }}
            ></a2k-autocomplete>`
      }</label
    >`;
  }
  private validateReferences(selector: string) {
    return [
      ...this.querySelectorAll<KnowledgeReference>(
        `${selector} knowledge-reference`,
      ),
    ]
      .map((picker) => picker.reportValidity())
      .every(Boolean);
  }
  private referenceField(
    name: string,
    value: unknown,
    update: (value: string | string[]) => void,
    context: JsonObject = {},
    required = false,
    multiple = false,
  ) {
    const values = referenceValues(value);
    return html`<label class="native-field"
      ><span>${this.label(name)}${required ? " *" : ""}</span>
      <knowledge-reference
        .resource=${referenceResources[name]}
        .label=${this.label(name)}
        .values=${values}
        .multiple=${multiple}
        .required=${required}
        .repository=${name === "repository" || name === "repositories" ? "" : String(context.repository || "")}
        .namespace=${name === "namespace" ? "" : String(context.namespace || "")}
        .fileId=${name === "file_id" ? "" : String(context.file_id || "")}
        @reference-change=${(event: CustomEvent<string[]>) => update(multiple ? event.detail : event.detail[0] || "")}
      ></knowledge-reference>
    </label>`;
  }
  private locationField(
    record: JsonObject,
    update: (value: JsonObject) => void,
    required = false,
  ) {
    const location = record.location as JsonObject | undefined;
    const change = (name: string, value: unknown) =>
      update({ location_id: "", location: { ...location, [name]: value } });
    return html`<fieldset class="knowledge-subform knowledge-form-wide">
      <legend>${this.label("location_id")}${required ? " *" : ""}</legend>
      <label class="knowledge-check"
        ><input
          type="checkbox"
          .checked=${Boolean(location)}
          @change=${(event: Event) => {
            const checked = (event.target as HTMLInputElement).checked;
            update({
              location_id: "",
              location: checked
                ? { namespace: "", relative_path: "" }
                : undefined,
            });
          }}
        />
        ${i18n.t("knowledgeNewLocation")}</label
      >
      ${
        location
          ? html`<div class="knowledge-form-grid">
              ${this.referenceField("namespace", location.namespace, (value) => change("namespace", value), {}, true)}
              <label class="native-field"
                ><span>${this.label("relative_path")} *</span
                ><knowledge-path-input
                  .namespace=${String(location.namespace || "")}
                  .label=${this.label("relative_path")}
                  .value=${String(location.relative_path || "")}
                  @path-change=${(event: CustomEvent<string>) => change("relative_path", event.detail)}
                ></knowledge-path-input
              ></label>
              ${[
                "line_start",
                "line_end",
                "page_start",
                "page_end",
                "section",
              ].map(
                (name) =>
                  html`<label class="native-field"
                    ><span>${this.label(name)}</span>
                    <input
                      class="a2k-native-input"
                      type=${name === "section" ? "text" : "number"}
                      min="1"
                      .value=${String(location[name] ?? "")}
                      @input=${(event: Event) => {
                        const value = (event.target as HTMLInputElement).value;
                        change(
                          name,
                          name === "section"
                            ? value
                            : value
                              ? Number(value)
                              : undefined,
                        );
                      }}
                  /></label>`,
              )}
            </div>`
          : this.referenceField(
              "location_id",
              record.location_id,
              (value) => update({ location_id: value, location: undefined }),
              record,
              required,
            )
      }
    </fieldset>`;
  }
  private payload() {
    const p: JsonObject = {};
    for (const def of fields[this.view as Resource] || []) {
      const v = this.editor?.[def.name];
      if (
        this.editing &&
        v === "" &&
        !def.required &&
        ["component", "location_id", "symbol_id"].includes(def.name)
      )
        p[def.name] = null;
      if (v !== undefined && v !== "")
        p[def.name] = ["repositories", "contracts", "evidence"].includes(
          def.name,
        )
          ? referenceValues(v)
          : v;
    }
    if (this.editor?.location) {
      p.location = this.editor.location;
      delete p.location_id;
    }
    if (this.view === "contracts") {
      const family = String(p.family || this.editing?.family || "declaration");
      const detail: JsonObject = {};
      for (const def of details[family] || []) {
        const v = this.editor?.[def.name];
        if (v !== undefined && v !== "") detail[def.name] = v;
      }
      p.detail = detail;
      p.evidence = this.evidenceRows
        .filter((x) => x.id)
        .map((x) => ({ id: x.id, relation: x.relation || "supports" }));
    }
    if (this.editing && ["contracts", "cases"].includes(this.view))
      p.expected_version = this.editing.version;
    return p;
  }
  private async save() {
    if (!this.editor || !this.validateReferences(".knowledge-editor")) return;
    this.busy = true;
    this.error = "";
    const wasEditing = Boolean(this.editing);
    try {
      if (this.editing)
        await api.updateKnowledgeResource(
          this.view,
          String(this.editing.id),
          this.payload(),
          String(this.editing.repository || ""),
        );
      else await api.createKnowledgeResource(this.view, this.payload());
      this.editor = null;
      this.editing = null;
      this.notice = i18n.t(
        wasEditing ? "knowledgeUpdated" : "knowledgeCreated",
      );
      await this.load(this.page);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.busy = false;
    }
  }
  private async deleteSelected() {
    if (!this.confirmDelete) return;
    this.busy = true;
    try {
      await api.deleteKnowledgeResource(
        this.view,
        String(this.confirmDelete.id),
        String(this.confirmDelete.repository || ""),
      );
      this.confirmDelete = null;
      this.notice = i18n.t("knowledgeDeleted");
      await this.load(this.page);
    } catch (e) {
      this.error = `${i18n.t("knowledgeDeleteFailed")}: ${(e as Error).message}`;
      this.confirmDelete = null;
    } finally {
      this.busy = false;
    }
  }
  private async saveRelation() {
    if (
      !this.relationEditor ||
      !this.editing ||
      !this.validateReferences(".knowledge-relation-editor")
    )
      return;
    const p = withLocation(this.relation);
    if (this.relationEditor === "consumers")
      p.observations = this.observations
        .filter((x) => Object.values(x).some(Boolean))
        .map(withLocation);
    try {
      await api.createContractRelation(
        String(this.editing.id),
        this.relationEditor,
        p,
      );
      this.relationEditor = null;
      this.notice = i18n.t("knowledgeRelationCreated");
    } catch (e) {
      this.error = (e as Error).message;
    }
  }
  private async deleteRelation(
    relation: "behaviors" | "consumers",
    item: JsonObject,
  ) {
    if (!this.editing) return;
    try {
      await api.deleteContractRelation(
        String(this.editing.id),
        relation,
        String(item.id),
      );
      const current = this.editing;
      await this.hydrateEditor(current);
      this.notice = i18n.t("knowledgeRelationDeleted");
    } catch (e) {
      this.error = (e as Error).message;
    }
  }
  private relatedRecords(relation: "behaviors" | "consumers") {
    const values = this.editing?.[relation];
    if (!Array.isArray(values) || !values.length) return nothing;
    return html`<fieldset class="knowledge-subform knowledge-form-wide">
      <legend>
        ${i18n.t(
          relation === "behaviors"
            ? "knowledgeBehaviors"
            : "knowledgeConsumers",
        )}
      </legend>
      ${values.map(
        (item) =>
          html`<div class="knowledge-related-record">
            ${this.detailObject(item as JsonObject)}
            <a2k-button
              @click=${() => void this.deleteRelation(relation, item as JsonObject)}
              >${i18n.t("knowledgeDeleteAction")}</a2k-button
            >
          </div>`,
      )}
    </fieldset>`;
  }
  private evidenceRow(row: JsonObject, i: number) {
    const update = (key: string, value: string) => {
      this.evidenceRows = this.evidenceRows.map((item, n) =>
        n === i ? { ...item, [key]: value } : item,
      );
    };
    return html`<div class="knowledge-row">
      ${this.referenceField("evidence_id", row.id, (value) => update("id", String(value)), this.editor || {}, true)}
      <input
        class="a2k-native-input"
        aria-label=${this.label("relation")}
        .value=${String(row.relation || "")}
        @input=${(e: Event) => update("relation", (e.target as HTMLInputElement).value)}
      />
      <button
        type="button"
        class="icon-action"
        title=${i18n.t("remove")}
        @click=${() => (this.evidenceRows = this.evidenceRows.filter((_, n) => n !== i))}
      >
        ×
      </button>
    </div>`;
  }
  private observationRow(row: JsonObject, i: number) {
    const names = [
      "location_id",
      "evidence_id",
      "ingestion_source",
      "observed_commit",
    ];
    return html`<div class="knowledge-row knowledge-row-four">
      ${names.map((name) => {
        const update = (value: unknown) =>
          (this.observations = this.observations.map((item, n) =>
            n === i ? { ...item, [name]: value } : item,
          ));
        if (name === "location_id")
          return this.locationField(
            row,
            (value) =>
              (this.observations = this.observations.map((item, n) =>
                n === i ? { ...item, ...value } : item,
              )),
          );
        if (referenceResources[name])
          return this.referenceField(name, row[name], update, this.relation);
        return html`<input
          class="a2k-native-input"
          aria-label=${this.label(name)}
          placeholder=${this.label(name)}
          .value=${String(row[name] || "")}
          @input=${(event: Event) => update((event.target as HTMLInputElement).value)}
        />`;
      })}
      <button
        type="button"
        class="icon-action"
        @click=${() => (this.observations = this.observations.filter((_, n) => n !== i))}
      >
        ×
      </button>
    </div>`;
  }
  private editorForm() {
    if (!this.editor) return nothing;
    const family = String(
      this.editor.family || this.editing?.family || "declaration",
    );
    return html`<form
      class="knowledge-editor"
      @submit=${(e: Event) => {
        e.preventDefault();
        void this.save();
      }}
    >
      <div class="knowledge-form-grid">
        ${fields[this.view as Resource]?.map((d) => this.input(d))}${
          this.view === "contracts"
            ? html`<fieldset class="knowledge-subform knowledge-form-wide">
                  <legend>
                    ${i18n.t("knowledgeContractDetail")} · ${family}
                  </legend>
                  <div class="knowledge-form-grid">
                    ${(details[family] || []).map((d) => this.input(d))}
                  </div>
                </fieldset>
                <fieldset class="knowledge-subform knowledge-form-wide">
                  <legend>${i18n.t("knowledgeEvidenceLinks")}</legend>
                  ${this.evidenceRows.map((row, i) => this.evidenceRow(row, i))}<a2k-button
                    type="button"
                    @click=${() => (this.evidenceRows = [...this.evidenceRows, { id: "", relation: "supports" }])}
                    >${i18n.t("knowledgeAddEvidence")}</a2k-button
                  >
                </fieldset>`
            : nothing
        }
        ${
          this.view === "contracts" && this.editing
            ? html`${this.relatedRecords("behaviors")}${this.relatedRecords("consumers")}`
            : nothing
        }
      </div>
    </form>`;
  }
  private relationForm() {
    if (!this.relationEditor) return nothing;
    const behavior = [
      "kind",
      "location_id",
      "symbol_id",
      "evidence_id",
      "trigger_condition",
      "old_observable",
      "new_observable",
      "consumer_dependency_risk",
      "confidence",
    ];
    const consumer = [
      "repository",
      "component",
      "file_id",
      "symbol_id",
      "symbol_name",
      "relation",
      "confidence",
    ];
    const names = this.relationEditor === "behaviors" ? behavior : consumer;
    return html`<div class="knowledge-form-grid knowledge-relation-editor">
      ${names.map((name) => {
        if (name === "location_id")
          return this.locationField(
            this.relation,
            (value) => (this.relation = { ...this.relation, ...value }),
          );
        if (referenceResources[name])
          return this.referenceField(
            name,
            this.relation[name],
            (value) => {
              this.relation = { ...this.relation, [name]: value };
              if (name === "repository")
                for (const field of [
                  "component",
                  "file_id",
                  "location_id",
                  "symbol_id",
                  "evidence_id",
                ])
                  this.relation[field] = "";
            },
            this.relation,
          );
        return html`<label class="native-field"
          ><span>${this.label(name)}</span
          ><input
            class="a2k-native-input"
            .value=${String(this.relation[name] || "")}
            @input=${(e: Event) => {
              this.relation = {
                ...this.relation,
                [name]: (e.target as HTMLInputElement).value,
              };
            }}
        /></label>`;
      })}${
        this.relationEditor === "behaviors"
          ? html`<label class="knowledge-check"
              ><input
                type="checkbox"
                @change=${(e: Event) => {
                  this.relation = {
                    ...this.relation,
                    compatibility_sensitive: (e.target as HTMLInputElement)
                      .checked,
                  };
                }}
              /><span>${this.label("compatibility_sensitive")}</span></label
            >`
          : html`<fieldset class="knowledge-subform knowledge-form-wide">
              <legend>${i18n.t("knowledgeObservations")}</legend>
              ${this.observations.map((row, i) => this.observationRow(row, i))}<a2k-button
                @click=${() => (this.observations = [...this.observations, {}])}
                >${i18n.t("knowledgeAddObservation")}</a2k-button
              >
            </fieldset>`
      }
    </div>`;
  }
  private tabLabel(tab: ViewKey) {
    return i18n.t(`knowledgeTab_${tab}` as never);
  }
  private sortColumn(key: string) {
    if (this.view === "search") return;
    this.sorts = {
      ...this.sorts,
      [this.view]: nextTableSort(this.sorts[this.view], key),
    };
    void this.load(1);
  }
  private rowActions(item: JsonObject, mutable: boolean) {
    return html`<div class="knowledge-actions">
      <a2k-button @click=${() => this.openDetail(item)}
        >${i18n.t("details")}</a2k-button
      >
      ${
        mutable
          ? html`<a2k-button @click=${() => this.startEdit(item)}
                >${i18n.t("edit")}</a2k-button
              ><a2k-button @click=${() => (this.confirmDelete = item)}
                >${i18n.t("knowledgeDeleteAction")}</a2k-button
              >`
          : nothing
      }
    </div>`;
  }
  private architectureView() {
    return html`<section
      class="knowledge-architecture"
      aria-labelledby="knowledge-architecture-title"
    >
      <header class="knowledge-architecture-heading">
        <h2 id="knowledge-architecture-title">
          ${i18n.t("knowledgeArchitectureTitle")}
        </h2>
        <p>${i18n.t("knowledgeArchitectureDescription")}</p>
      </header>
      <p>${i18n.t("knowledgeArchitectureRelationNote")}</p>
      <div class="knowledge-architecture-map">
        <svg
          viewBox="0 0 1060 510"
          role="img"
          aria-label=${i18n.t("knowledgeArchitectureMapLabel")}
        >
          <defs>
            <marker
              id="knowledge-relation-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#000080" />
            </marker>
          </defs>
          <g class="knowledge-relation-lines">
            <path d="M 110 122 V 200" />
            <path d="M 200 232 H 290" />
            <path d="M 470 232 H 505 V 60 H 540" />
            <path d="M 505 232 V 175 H 540" />
            <path d="M 505 232 V 290 H 540" />
            <path d="M 505 290 V 405 H 540" />
            <path d="M 630 92 V 143" />
            <path d="M 630 207 V 258" />
            <path d="M 630 322 V 373" />
            <path d="M 720 290 H 785 V 333 H 840" />
            <path d="M 720 405 H 785 V 355 H 840" />
          </g>
          <g class="knowledge-relation-labels">
            <text x="122" y="163">
              ${i18n.t("knowledgeArchitectureSourceLink")}
            </text>
            <text x="215" y="217">file_id</text>
            <text x="305" y="292">
              ${i18n.t("knowledgeArchitectureLocationLink")}
            </text>
            <text x="305" y="311">location_id</text>
            <text x="638" y="123">chunk_symbol</text>
            <text x="638" y="238">symbol_id</text>
            <text x="638" y="353">contract_evidence</text>
            <text x="814" y="260">case_contract</text>
            <text x="814" y="425">case_evidence</text>
          </g>
          ${(
            [
              ["repositories", 20, 58, "repository"],
              ["files", 20, 200, "file"],
              ["locations", 290, 200, "source_location"],
              ["chunks", 540, 28, "chunk"],
              ["symbols", 540, 143, "symbol"],
              ["contracts", 540, 258, "contract"],
              ["evidence", 540, 373, "evidence"],
              ["cases", 840, 312, "analysis_case"],
            ] as [ViewKey, number, number, string][]
          ).map(
            ([tab, x, y, table]) => svg`
            <a href="#knowledge" @click=${(event: Event) => {
              event.preventDefault();
              this.selectView(tab);
            }}
              aria-label=${this.tabLabel(tab)} class="knowledge-relation-node">
              <rect x=${x} y=${y} width="180" height="64" rx="2" />
              <text x=${x + 90} y=${y + 26}>${this.tabLabel(tab)}</text>
              <text class="knowledge-relation-table" x=${x + 90} y=${y + 47}>${table}</text>
            </a>`,
          )}
          <text class="knowledge-relation-caption" x="840" y="465">
            ${i18n.t("knowledgeArchitectureCaseLink")}
          </text>
        </svg>
      </div>
      <div class="knowledge-architecture-views">
        <h3>${i18n.t("knowledgeArchitectureViewsTitle")}</h3>
        <p>${i18n.t("knowledgeArchitectureViewsDescription")}</p>
        <div class="knowledge-view-links">
          ${(
            [
              "contracts",
              "evidence",
              "symbols",
              "chunks",
              "files",
              "repositories",
            ] as ViewKey[]
          ).map(
            (tab) =>
              html`<button type="button" @click=${() => this.selectView(tab)}>
                ${this.tabLabel(tab)}
              </button>`,
          )}
        </div>
      </div>
    </section>`;
  }
  render() {
    const tabs: ViewKey[] = [
      "search",
      "contracts",
      "evidence",
      "symbols",
      "chunks",
      "files",
      "locations",
      "components",
      "repositories",
      "cases",
      "ingestion-sources",
    ];
    const filterNames =
      this.view === "search"
        ? [
            "q",
            "types",
            "match",
            ...(this.query.match === "minimum" ? ["minimum_should_match"] : []),
            "repository",
            "component",
            "kind",
            "risk_surface",
            "ingestion_source",
          ]
        : filters[this.view] || [];
    const tableColumns =
      this.view === "search"
        ? [
            "resource_type",
            "title",
            "summary",
            "repository",
            "component",
            "kind",
            "citation",
          ]
        : columns[this.view];
    const mutable =
      this.view !== "search" &&
      this.view !== "ingestion-sources" &&
      this.view !== "documents";
    const repositorySelection = this.view === "repositories";
    const admin = session.user?.role === "administrator";
    return html`<section class="knowledge-console">
      <div class="knowledge-header">
        <div>
          <h1>${i18n.t("knowledgeTitle")}</h1>
          <p>${i18n.t("knowledgeDescription")}</p>
        </div>
        <strong
          >${i18n.t("knowledgeResultCount", { count: this.totalCount })}</strong
        >
      </div>
      ${
        mutable
          ? html`<section class="knowledge-management">
              ${
                this.view === "files"
                  ? html`<a2k-button @click=${this.startUpload}
                      >${i18n.t("knowledgeUploadFile")}</a2k-button
                    >`
                  : nothing
              }
              <a2k-button @click=${this.startCreate}
                >${i18n.t("knowledgeCreate")}</a2k-button
              >
              ${
                repositorySelection
                  ? html`<a2k-button
                        ?disabled=${!admin || this.busy || !this.selectedRepositories.size}
                        @click=${() => this.openKnowledgeUpdate()}
                        >${i18n.t("knowledgeUpdateSelected", { count: this.selectedRepositories.size })}</a2k-button
                      >${!admin ? html`<p>${i18n.t("knowledgeUpdateAdminOnly")}</p>` : nothing}`
                  : nothing
              }
            </section>`
          : nothing
      }
      <div class="knowledge-tabs" role="tablist">
        ${tabs.map(
          (tab) =>
            html`<button
              type="button"
              role="tab"
              aria-selected=${String(this.view === tab)}
              class=${this.view === tab ? "is-active" : ""}
              @click=${() => this.selectView(tab)}
            >
              ${this.tabLabel(tab)}
            </button>`,
        )}
      </div>
      ${
        this.view === "search"
          ? this.architectureView()
          : html`<form
              class="knowledge-filters"
              @submit=${(e: Event) => {
                e.preventDefault();
                if (this.validateReferences(".knowledge-filters"))
                  void this.load(1);
              }}
            >
              ${filterNames.map((n) => this.filter(n))}<label
                class="native-field knowledge-page-size"
                ><span>${i18n.t("knowledgePageSize")}</span
                ><select
                  class="a2k-native-input"
                  .value=${String(this.pageSize)}
                  @change=${(e: Event) => {
                    this.pageSize = Number(
                      (e.target as HTMLSelectElement).value,
                    );
                    if (this.view !== "search" || this.query.q)
                      void this.load(1);
                  }}
                >
                  <option>10</option>
                  <option>20</option>
                  <option>50</option>
                  <option>100</option>
                </select></label
              ><a2k-button type="submit" ?disabled=${this.busy}
                >${i18n.t("knowledgeSearch")}</a2k-button
              >
            </form>`
      }
      ${
        this.error
          ? html`<p class="status status-error" role="alert">${this.error}</p>`
          : nothing
      }
      ${
        this.notice
          ? html`<p class="status status-success">${this.notice}</p>`
          : nothing
      }
      ${
        this.view === "search"
          ? nothing
          : html`<div class="knowledge-table table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    ${
                      repositorySelection
                        ? html`<th>
                            <input
                              type="checkbox"
                              aria-label=${i18n.t("selectAll")}
                              ?disabled=${!admin || this.busy}
                              .checked=${
                                this.items.length > 0 &&
                                this.items.every((item) =>
                                  this.selectedRepositories.has(
                                    String(item.id),
                                  ),
                                )
                              }
                              .indeterminate=${
                                this.items.some((item) =>
                                  this.selectedRepositories.has(
                                    String(item.id),
                                  ),
                                ) &&
                                !this.items.every((item) =>
                                  this.selectedRepositories.has(
                                    String(item.id),
                                  ),
                                )
                              }
                              @change=${(event: Event) =>
                                this.selectRepositoryPage(
                                  (event.target as HTMLInputElement).checked,
                                )}
                            />
                          </th>`
                        : nothing
                    }
                    ${tableColumns.map((c) =>
                      this.view === "search" || c === "has_markdown"
                        ? html`<th>${this.label(c)}</th>`
                        : sortableTableHeader(
                            this.label(c),
                            this.view === "documents" && c === "relative_path"
                              ? "path"
                              : c,
                            this.sorts[this.view],
                            (key) => this.sortColumn(key),
                          ),
                    )}
                    <th>${i18n.t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  ${
                    this.busy
                      ? html`<tr>
                          <td
                            colspan=${tableColumns.length + 1 + Number(repositorySelection)}
                            aria-busy="true"
                          >
                            <table-loading
                              label=${i18n.t("loading")}
                            ></table-loading>
                          </td>
                        </tr>`
                      : this.items.length
                        ? this.items.map(
                            (item) =>
                              html`<tr>
                                ${
                                  repositorySelection
                                    ? html`<td>
                                        <input
                                          type="checkbox"
                                          aria-label=${String(item.name || item.id)}
                                          ?disabled=${!admin}
                                          .checked=${this.selectedRepositories.has(String(item.id))}
                                          @change=${(event: Event) =>
                                            this.selectRepository(
                                              item,
                                              (event.target as HTMLInputElement)
                                                .checked,
                                            )}
                                        />
                                      </td>`
                                    : nothing
                                }
                                ${tableColumns.map(
                                  (c) =>
                                    html`<td title=${readable(item[c])}>
                                      ${readable(item[c])}
                                    </td>`,
                                )}
                                <td>${this.rowActions(item, mutable)}</td>
                              </tr>`,
                          )
                        : html`<tr>
                            <td
                              colspan=${tableColumns.length + 1 + Number(repositorySelection)}
                            >
                              ${i18n.t("knowledgeEmpty")}
                            </td>
                          </tr>`
                  }
                </tbody>
              </table>
            </div>`
      }
      <app-pagination
        .page=${this.page}
        .totalPages=${this.totalPages}
        .busy=${this.busy}
        @page-change=${async (e: CustomEvent<{ page: number }>) => {
          const scroller = document.querySelector<HTMLElement>(".workspace");
          const scrollTop = scroller?.scrollTop ?? window.scrollY;
          await this.load(e.detail.page);
          await this.updateComplete;
          if (scroller) scroller.scrollTop = scrollTop;
          else window.scrollTo({ top: scrollTop, behavior: "instant" });
        }}
      ></app-pagination>
      <app-dialog
        class=${this.view === "files" && this.detail?.text ? "wide-dialog" : ""}
        .open=${Boolean(this.detail)}
        heading=${i18n.t("knowledgeRecordDetail")}
        cancel-label=${i18n.t("close")}
        .hideConfirm=${true}
        @dialog-cancel=${() => {
          this.detailSequence += 1;
          this.detail = null;
        }}
        >${
          this.detailBusy
            ? html`<a2k-progress
                aria-label=${i18n.t("loading")}
              ></a2k-progress>`
            : this.detail
              ? html`<div class="knowledge-detail dialog-content-container">
                  ${this.detailObject(this.detail)}
                </div>`
              : nothing
        }</app-dialog
      ><app-dialog
        .open=${Boolean(this.editor)}
        heading=${i18n.t(this.editing ? "knowledgeEditTitle" : "knowledgeCreateTitle")}
        confirm-label=${i18n.t("save")}
        cancel-label=${i18n.t("cancel")}
        .busy=${this.busy}
        @dialog-cancel=${() => {
          this.editor = null;
          this.editing = null;
        }}
        @dialog-confirm=${() => void this.save()}
        >${this.editorForm()}${
          this.view === "contracts" && this.editing
            ? html`<div class="knowledge-relation-actions">
                <a2k-button
                  @click=${() => {
                    this.relationEditor = "behaviors";
                    this.relation = {};
                  }}
                  >${i18n.t("knowledgeAddBehavior")}</a2k-button
                ><a2k-button
                  @click=${() => {
                    this.relationEditor = "consumers";
                    this.relation = {};
                    this.observations = [{}];
                  }}
                  >${i18n.t("knowledgeAddConsumer")}</a2k-button
                >
              </div>`
            : nothing
        }</app-dialog
      >
      <app-dialog
        .open=${Boolean(this.relationEditor)}
        heading=${i18n.t(this.relationEditor === "behaviors" ? "knowledgeAddBehavior" : "knowledgeAddConsumer")}
        confirm-label=${i18n.t("save")}
        cancel-label=${i18n.t("cancel")}
        @dialog-cancel=${() => (this.relationEditor = null)}
        @dialog-confirm=${() => void this.saveRelation()}
        >${this.relationForm()}</app-dialog
      ><app-dialog
        .open=${Boolean(this.confirmDelete)}
        heading=${i18n.t("knowledgeDeleteTitle")}
        confirm-label=${i18n.t("knowledgeDeleteAction")}
        cancel-label=${i18n.t("cancel")}
        .busy=${this.busy}
        message=${i18n.t("knowledgeHardDeleteHint")}
        @dialog-cancel=${() => (this.confirmDelete = null)}
        @dialog-confirm=${() => void this.deleteSelected()}
        ><p>
          ${i18n.t("knowledgeConfirmDelete", { id: String(this.confirmDelete?.id || "") })}
        </p></app-dialog
      >
      <app-dialog
        .open=${this.showUploadDialog}
        heading=${i18n.t("knowledgeUploadFile")}
        confirm-label=${i18n.t("upload")}
        cancel-label=${i18n.t("cancel")}
        .busy=${this.busy}
        @dialog-cancel=${() => (this.showUploadDialog = false)}
        @dialog-confirm=${() => void this.submitUpload()}
      >
        ${
          this.uploadError
            ? html`<p
                class="message"
                style="color: #8b1717; background: #f5dddd; padding: 9px;"
              >
                ${this.uploadError}
              </p>`
            : nothing
        }
        <label class="native-field">
          <span>${i18n.t("file")} *</span>
          <input
            type="file"
            class="a2k-native-input"
            accept=".pdf,.docx,.md,.txt"
            @change=${this.handleFileSelect}
          />
        </label>
        <label class="native-field">
          <span>${i18n.t("namespace")} *</span>
          <knowledge-reference
            class="upload-namespace"
            resource="namespace"
            .label=${i18n.t("namespace")}
            .values=${this.uploadNamespace ? [this.uploadNamespace] : []}
            .required=${true}
            @reference-change=${(event: CustomEvent<string[]>) => (this.uploadNamespace = event.detail[0] || "")}
          ></knowledge-reference>
        </label>
        <label class="native-field">
          <span>${i18n.t("relative_path")}</span>
          <input
            type="text"
            class="a2k-native-input"
            .value=${this.uploadRelativePath}
            @input=${(e: Event) => (this.uploadRelativePath = (e.target as HTMLInputElement).value)}
            placeholder="${this.uploadFile?.name || ""}"
          />
        </label>
      </app-dialog>
      <knowledge-update-dialog
        .open=${this.updateOpen}
        .repositories=${this.updateRepositories}
        @update-close=${() => {
          this.updateOpen = false;
        }}
        @update-completed=${() => {
          if (this.view !== "search") void this.load();
        }}
      ></knowledge-update-dialog>
    </section>`;
  }
}
